import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { st, classes } from './SidePanel.st.css';
import Header from './components/Header';
import Content from './components/Content';
import Footer from './components/Footer';
import Divider from './components/Divider';
import Section from './components/Section';
import Field from './components/Field';
import { SidePanelContext } from './SidePanelAPI';
import { SidePanelProps } from './SidePanel.types';
import { TooltipCommonProps } from '../common/PropTypes/TooltipCommon';
import deprecationLog from '../utils/deprecationLog';

class SidePanel extends PureComponent<SidePanelProps> {
  static displayName = 'SidePanel';

  static Header = Header;
  static Content = Content;
  static Footer = Footer;
  static Divider = Divider;
  static Section = Section;
  static Field = Field;

  static propTypes = {
    className: PropTypes.string,
    dataHook: PropTypes.string,
    closeButtonProps: PropTypes.shape({
      ariaLabel: PropTypes.string,
      onClick: PropTypes.func,
      onMouseDown: PropTypes.func,
      size: PropTypes.oneOf(['small', 'medium', 'large']),
      skin: PropTypes.oneOf([
        'standard',
        'standardFilled',
        'light',
        'lightFilled',
        'dark',
        'transparent',
      ]),
    }),
    helpButtonProps: PropTypes.shape({
      ariaLabel: PropTypes.string,
      onClick: PropTypes.func,
      onMouseDown: PropTypes.func,
      size: PropTypes.oneOf(['small', 'medium', 'large']),
      skin: PropTypes.oneOf([
        'standard',
        'standardFilled',
        'light',
        'lightFilled',
        'dark',
        'transparent',
      ]),
    }),
    children: PropTypes.node,
    width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    maxHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    skin: PropTypes.oneOf(['standard', 'floating']),
    draggable: PropTypes.bool,
    backButtonProps: PropTypes.shape({
      ariaLabel: PropTypes.string,
      onClick: PropTypes.func,
      onMouseDown: PropTypes.func,
      description: PropTypes.node,
    }),

    /**
     * @deprecated use (backButtonProps/closeButtonProps/helpButtonProps) instead
     */
    onCloseButtonClick: PropTypes.func,
    onHelpButtonClick: PropTypes.func,
    onBackButtonClick: PropTypes.func,
    backButtonDescription: PropTypes.node,
    backButtonTooltipProps: PropTypes.shape(TooltipCommonProps),
  };

  static defaultProps = {
    width: '420px',
    height: '100%',
  };

  render() {
    const {
      dataHook,
      skin = 'standard',
      children,
      className,
      width,
      height,
      maxHeight,
      draggable,

      // Deprecated Props
      backButtonDescription,
      backButtonTooltipProps,
      onBackButtonClick,
      onCloseButtonClick,
      onHelpButtonClick,
    } = this.props;

    if (backButtonDescription) {
      deprecationLog(
        '<SidePanel /> props "backButtonDescription" is deprecated and will be removed in next major release, please use "backButtonProps.description" instead',
      );
    }

    if (backButtonTooltipProps) {
      deprecationLog(
        '<SidePanel /> props "backButtonTooltipProps" is deprecated and will be removed in next major release, please use "backButtonProps.tooltipProps" instead',
      );
    }

    if (onBackButtonClick) {
      deprecationLog(
        '<SidePanel /> props "onBackButtonClick" is deprecated and will be removed in next major release, please use "backButtonProps.onClick" instead',
      );
    }

    if (onCloseButtonClick) {
      deprecationLog(
        '<SidePanel /> props "onCloseButtonClick" is deprecated and will be removed in next major release, please use "closeButtonProps.onClick" instead',
      );
    }

    if (onHelpButtonClick) {
      deprecationLog(
        '<SidePanel /> props "onHelpButtonClick" is deprecated and will be removed in next major release, please use "helpButtonProps.onClick" instead',
      );
    }

    const helpButtonProps = {
      ...this.props.helpButtonProps,
      onClick: this.props.helpButtonProps?.onClick || onHelpButtonClick,
    };

    const closeButtonProps = {
      ...this.props.closeButtonProps,
      onClick: this.props.closeButtonProps?.onClick || onCloseButtonClick,
    };

    const backButtonProps = {
      ...this.props.backButtonProps,
      onClick: this.props.backButtonProps?.onClick || onBackButtonClick,
      description:
        this.props.backButtonProps?.description || backButtonDescription,
      tooltipProps:
        this.props.backButtonProps?.tooltipProps || backButtonTooltipProps,
    };

    const isDraggable = draggable && skin === 'floating';

    return (
      <SidePanelContext.Provider
        value={{
          skin,
          isDraggable,
          closeButtonProps,
          helpButtonProps,
          backButtonProps,
        }}
      >
        <div
          className={st(classes.root, { skin }, className)}
          data-hook={dataHook}
          style={{ width, height, maxHeight }}
        >
          {children}
        </div>
      </SidePanelContext.Provider>
    );
  }
}

export default SidePanel;
